import { computed, ref } from 'vue'
import { dateTimeFieldConfig } from './dateFieldPresets'
import { api } from '@/global/services/api'

const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/work_order_details'

const projectStatusItems = ref([])
const projectTypeItems = ref([])
const cityDistrictItems = ref([])
let projectStatusItemsRequested = false
let projectTypeItemsRequested = false
let cityDistrictItemsRequested = false

function fetchProjectStatusItems () {
  if (projectStatusItemsRequested) {
    return
  }
  projectStatusItemsRequested = true

  api('road-maintenance').get('statuses', { types: ['PROJECT'] })
    .then(({ data }) => {
      projectStatusItems.value = data?.project || []
    })
    .catch((exception) => {
      console.error(exception)
      projectStatusItemsRequested = false
    })
}

function fetchProjectTypeItems () {
  if (projectTypeItemsRequested) {
    return
  }
  projectTypeItemsRequested = true

  api('road-maintenance').get('codebook', { types: ['PROJECT_TYPE'] })
    .then(({ data }) => {
      projectTypeItems.value = data?.project_type || []
    })
    .catch((exception) => {
      console.error(exception)
      projectTypeItemsRequested = false
    })
}

function fetchCityDistrictItems () {
  if (cityDistrictItemsRequested) {
    return
  }
  cityDistrictItemsRequested = true

  api('road-maintenance').get('traffic-regulation/city-districts')
    .then(({ data }) => {
      cityDistrictItems.value = data || []
    })
    .catch((exception) => {
      console.error(exception)
      cityDistrictItemsRequested = false
    })
}

const COLUMNS_COUNT = 4

// FieldsRenderer binds every field to a flat `formData[key]` entry, so each of
// the project's roads gets its own `road_name_{index}` key on `request_details`
// (see `mapProjectSummary` in useWorkOrderDetails.js) and one read-only text
// field per road here.
export const ROAD_NAME_KEY_PREFIX = 'road_name_'

export function useRequestDetailsFields (options) {
  const { translate, isEditing, getRoads } = options || {}

  fetchProjectStatusItems()
  fetchProjectTypeItems()
  fetchCityDistrictItems()

  const roadFields = computed(() => {
    const roads = getRoads?.() || []

    return roads.map((_road, index) => ({
      key: `${ROAD_NAME_KEY_PREFIX}${index}`,
      label: translate(`${TRANSLATION_NAMESPACE}.road`),
      editable: false,
      visible: (vm) => !vm.edit
    }))
  })

  const fields = computed(() => {
    const allFields = [
      {
        key: 'status_id',
        label: translate(`${TRANSLATION_NAMESPACE}.status`),
        type: 'autocomplete',
        items: projectStatusItems.value,
        autocomplete_options: {
          custom_display_value: (item) => translate(item?.description || ''),
          allowSearch: false
        },
        editable: true,
        visible: true
      },
      {
        key: 'type_id',
        label: translate(`${TRANSLATION_NAMESPACE}.project_type`),
        type: 'autocomplete',
        items: projectTypeItems.value,
        autocomplete_options: {
          custom_display_value: (item) => translate(item?.description || ''),
          allowSearch: false
        },
        editable: true,
        visible: true
      },
      {
        ...dateTimeFieldConfig('planned_start', translate(`${TRANSLATION_NAMESPACE}.planned_start`)),
        editable: true,
        visible: true
      },
      {
        ...dateTimeFieldConfig('planned_end', translate(`${TRANSLATION_NAMESPACE}.planned_end`)),
        editable: true,
        visible: true
      },
      {
        key: 'address',
        label: translate(`${TRANSLATION_NAMESPACE}.location_description`),
        editable: true,
        visible: true
      },
      {
        key: 'city_district_id',
        label: translate(`${TRANSLATION_NAMESPACE}.city_district`),
        type: 'autocomplete',
        items: cityDistrictItems.value,
        autocomplete_options: {
          custom_display_value: (item) => item?.name || '',
          allowSearch: false
        },
        editable: false,
        visible: (vm) => !vm.edit
      },
      ...roadFields.value,
      {
        key: 'construction_site_id',
        label: translate(`${TRANSLATION_NAMESPACE}.construction_site`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'construction-sites/autocomplete'
        },
        editable: true,
        visible: true
      },
      {
        // Read-only display of an already formatted datetime string - a plain
        // text field, not a picker, since there is nothing to pick here.
        key: 'actual_start',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_start`),
        editable: false,
        visible: (vm) => !vm.edit
      },
      {
        key: 'actual_end',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_end`),
        editable: false,
        visible: (vm) => !vm.edit
      }
    ]

    // FieldsRenderer groups fields by their `row` and pads each row up to
    // `columnsCount` with blank placeholders - fixed row numbers can leave
    // gaps whenever some fields on a row are hidden (eg. entering edit mode).
    // Assign rows dynamically, after filtering to only the currently visible
    // fields, so they always pack 4-per-row with no gaps.
    const evalVm = { edit: !!isEditing?.() }

    return allFields
      .filter(field => {
        const { visible } = field
        return typeof visible === 'function' ? visible(evalVm) : !!visible
      })
      .map((field, index) => ({
        ...field,
        row: Math.floor(index / COLUMNS_COUNT),
        visible: true
      }))
  })

  return computed(() => ({
    title: translate(`${TRANSLATION_NAMESPACE}.request_details`),
    columnsCount: COLUMNS_COUNT,
    fields: fields.value
  }))
}
